// src/components/BundleCard.tsx

"use client";

import React, { useState, useRef } from "react";
import classNames from "classnames";
import { Star, StarHalf } from "lucide-react";
import Image from "next/image";
import { useTranslations } from "@/context/TranslationsContext";

// CollapsibleCountryTags (memoized) for showing roaming tags
const CollapsibleCountryTags: React.FC<{ items: string[]; limit?: number }> =
  React.memo(({ items, limit = 3 }) => {
    const [expanded, setExpanded] = useState(false);
    const ref = useRef<HTMLUListElement>(null);
    const shouldCollapse = items.length > limit;
    const visible = expanded || !shouldCollapse ? items : items.slice(0, limit);
    const toggle = () => {
      if (expanded && ref.current) {
        ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      setExpanded(!expanded);
    };

    return (
      <ul ref={ref} className="flex flex-wrap gap-2">
        {visible.map((n, i) => (
          <li
            key={i}
            className="px-3 py-1 bg-green-50 text-green-800 rounded-full text-sm"
          >
            {n}
          </li>
        ))}
        {shouldCollapse && (
          <li
            onClick={toggle}
            className="px-3 py-1 bg-transparent rounded-full text-xs cursor-pointer flex items-center"
            aria-expanded={expanded}
          >
            {!expanded && <span className="mr-1">…</span>}
            <span
              className={classNames(
                "text-blue-500 animate-pulse transition-transform duration-200",
                { "rotate-180": expanded }
              )}
            >
              ▼
            </span>
          </li>
        )}
      </ul>
    );
  });

interface BundleCardProps {
  bundle: any; // BundleWithCountries
  name: string;
  roaming: string[];
  price: string;
  rating: number;
  reviews: number;
  networks: string[];
  onBuy: () => void;
  t: ReturnType<typeof useTranslations>["t"];
  isRtl: boolean;
}

const BundleCard: React.FC<BundleCardProps> = React.memo(
  ({ bundle, name, roaming, price, rating, reviews, networks, onBuy, t, isRtl }) => (
    <div className="relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-shadow flex flex-col h-full my-4">
      <div className="relative aspect-video bg-gray-200">
        <Image
          src={bundle.imageUrl}
          alt={name}
          fill
          loading="lazy"
          sizes="(max-width: 768px) 100vw, 50vw"
          onError={(e) => {
            const img = e.currentTarget;
            img.onerror = null;
            img.src = "/default-background.jpg";
          }}
          className="object-cover"
        />
      </div>
      <div className="p-6 flex flex-col flex-grow">
        <h3 className="text-lg font-semibold mb-4">{name}</h3>

        <div className="flex items-center justify-between mb-4">
          <div
            className={classNames("flex items-center space-x-1", {
              "space-x-reverse": isRtl,
            })}
          >
            {Array.from({ length: Math.floor(rating) }).map((_, i) => (
              <Star
                key={i}
                className="w-5 h-5 text-yellow-400"
                fill="currentColor"
              />
            ))}
            {rating % 1 !== 0 && (
              <StarHalf
                className={classNames(
                  "w-5 h-5 text-yellow-400",
                  { "transform scale-x-[-1]": isRtl } // Flip in RTL
                )}
                fill="currentColor"
              />
            )}
            <span className="text-sm font-medium text-gray-700">
              {rating.toFixed(1)}
            </span>
            <span className="text-sm text-gray-500">({reviews})</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <span className="px-2 py-1 bg-gray-50 text-gray-800 rounded-full text-xs">
            {bundle.speed ? `${bundle.speed} Speed` : ""}
          </span>
          {bundle.autostart && (
            <span className="px-2 py-1 bg-blue-50 text-blue-800 rounded-full text-xs">
              Autostart
            </span>
          )}
          {bundle.unlimited && (
            <span className="px-2 py-1 bg-purple-50 text-purple-800 rounded-full text-xs">
              Unlimited
            </span>
          )}
        </div>

        {networks.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold mb-2">
              {t("countryPageClient.networkBrands")}
            </h4>
            <ul className="flex flex-wrap gap-2">
              {networks.map((b, i) => (
                <li
                  key={i}
                  className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-xs"
                  aria-label={t("countryPageClient.networkBrandAriaLabel", {
                    brand: b,
                  })}
                >
                  {b}
                </li>
              ))}
            </ul>
          </div>
        )}

        {roaming.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold mb-2">
              {t("countryPageClient.roamingCoverage")}
            </h4>
            <CollapsibleCountryTags items={roaming} limit={3} />
          </div>
        )}

        <button
          onClick={onBuy}
          className="mt-auto w-full px-4 py-2 bg-primary text-white text-lg font-semibold rounded-full hover:bg-accent transition"
          aria-label={t("countryPageClient.buyButtonAriaLabel", {
            bundleName: name,
          })}
        >
          {t("countryPageClient.buyNow")} – {price}
        </button>
      </div>
    </div>
  )
);

export default BundleCard;
